import React, { useState, useRef } from "react";
import { Group, Modal, Box, Divider, Text, ActionIcon, TextInput, Avatar, Loader, Alert, Button, Popover, Badge } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import Swal from "sweetalert2";
import pluralizeJs from "pluralize";

import FollowModel from "../../models/Follow";

import { useDebounce } from "../hooks/useDebounce";
import { colors as appColors } from "../../collections/colors";

export default function FollowsBlock({
  user,
  colors,
  disablePopover,
  followers_count,
  followees_count,
  prana_count,
  posts_in_count,
  likes_in_count,
  top_parent_name,
  onFetchFollowInfo
}) {
  const color = colors?.default_text_color || "#000";

  const [type, setType] = useState(null);
  const [term, setTerm] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(null);

  const page = useRef(1);
  const per = useRef(20);
  const noMore = useRef(false);

  const counts = [
    { label: pluralizeJs("follower", followers_count), value: followers_count, type: "followers" },
    { label: "following", value: followees_count, type: "following" },
    { label: "prana", value: prana_count, type: "prana" }
  ];

  const closePopover = () => {
    setOpened(null);
    setType(null);
    setTerm("");
    setData([]);
    setLoading(true);
  };

  const getData = (type, value) => {
    if (!user || noMore.current) return;
    setLoading(true);
    const action = type === "following" ? FollowModel.onFetchFollowees : FollowModel.onFetchFollowers;
    action(
      { user_id: user.username, term: value, per: per.current, page: page.current  },
      (res) => {
        if (Array.isArray(res)) {
          if (page.current === 1) {
            setData(res);
          } else {
            const merged = [...data, ...res];
            const unique = Array.from(
              new Map(merged.map((item) => [item.id, item])).values()
            );
            setData(unique);
          }
          if (res.length % per.current != 0) {
            noMore.current = true;
          }
        }
        setLoading(false);
      },
      (err) => setLoading(false)
    );
  };

  const openProfile = (username) => {
    window.open(`/${username}`, `_blank`);
  };

  const onPressFollow = (u) => {
    if (!user || !u) return;
    if (u.is_followed) {
      Swal.fire({
        title: "Are you sure?",
        html: `Unfollow @${u.username}?`,
        showDenyButton: true,
        confirmButtonText: "Unfollow",
        denyButtonText: "No",
        confirmButtonColor: appColors.red,
        denyButtonColor: appColors.gray
      }).then((result) => {
        if (result.isConfirmed) {
          onUpdateFollow(u);
        }
      });
    } else {
      onUpdateFollow(u);
    }
  };

  const onUpdateFollow = (u) => {
    const action = u.is_followed ? FollowModel.onUnfollow : FollowModel.onFollow;
    action(
      u.id,
      (res) => {
        if (res.success) {
          setData(prevState =>
            prevState.map(item =>
              item.id === u.id ? { ...item, is_followed: !item.is_followed } : item
            )
          );
          if (typeof onFetchFollowInfo === "function") {
            onFetchFollowInfo();
          }
        }
      },
      (err) => {}
    );
  }

  const debouncedSearch = useDebounce(({ type, value }) => {
    page.current = 1;
    noMore.current = false;
    getData(type, value);
  }, 500);

  return (
    <Group spacing={0} mb={10}>
      {counts.map((item, index) => (
        <Popover
          key={index}
          opened={opened === index}
          onClose={() => closePopover()}
          position="bottom"
          placement="center"
          withArrow
          trapFocus={false}
          closeOnEscape={false}
          transition="pop-top-left"
          // width={180}
          styles={{ body: { pointerEvents: 'none' } }}
          zIndex={999999}
          spacing={10}
          target={
            <Group
              spacing={0}
              mr={10}
              sx={{ cursor: item.type === "prana" ? undefined : "pointer" }}
              onClick={item.type === "prana" ? undefined : () => {
                setType(item.type);
                debouncedSearch({ type: item.type, value: "" });
              }}
              onMouseEnter={item.type === "prana" && !disablePopover ? () => setOpened(index) : undefined}
              onMouseLeave={item.type === "prana" && !disablePopover ? () => closePopover() : undefined}
            >
              <Text size="lg" weight={700} color={color} pr={5}>
                {item.value || 0}
              </Text>
              <Text weight={500} color={color}>{item.label}</Text>
            </Group>
          }
        >
          <Group spacing={0} sx={{ justifyContent: "center" }}>
            <Text size="lg" weight={700} mr={5}>{posts_in_count || 0}</Text>
            <Text size="sm" mr={10}>{"posts"}</Text>
            <Text size="lg" weight={700} mr={5}>{likes_in_count || 0}</Text>
            <Text size="sm">{"likes"}</Text>
          </Group>
          <Text size="xs" color="dimmed" weight={500} align="center">
            in {top_parent_name}
          </Text>
        </Popover>
      ))}
      
      <Modal
        opened={!!type}
        onClose={() => {
          setType(null);
          setTerm("");
          setData([]);
          setLoading(true);
        }}
        zIndex={999999}
        overflow="inside"
        padding={0}
        spacing={0}
        withCloseButton={false}
        styles={{ title: { width: "100%", marginRight: 10 }}}
        title={
          <>
            <Group spacing={0} p={10}>
              <Box style={{ flex: 1 }} />
              <Text weight={500}>
                {type === "followers"? "Followers" : "Following"}
              </Text>
              <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                <ActionIcon onClick={() => setType(null)}>
                  <FontAwesomeIcon icon={solid("xmark")} />
                </ActionIcon>
              </Box>
            </Group>

            <Divider />

            <TextInput
              value={term}
              placeholder="Search"
              px={20}
              pt={10}
              icon={<FontAwesomeIcon icon={solid("search")} color="gray" />}
              type="search"
              rightSection={
                term ? (
                  <Text
                    sx={{ cursor: "pointer" }}
                    onClick={() => setTerm("")}
                  >
                    <FontAwesomeIcon icon={solid("xmark")} color="gray" />
                  </Text>
                ) : (
                  <Text>{"  "}</Text>
                )
              }
              onChange={(e) => {
                setTerm(e.currentTarget.value);
                debouncedSearch({ type, value: e.currentTarget.value });
              }}
            />

          </>
        }
      >
        <Box px={20} pb={data.length > 0 ? 10 : 0}>
          {data.length === 0 && loading && (
            <Loader color="dark" sx={{ width: "100%" }} />
          )}
          {data.map((item, index) => {
            return (
              <Group key={index} spacing={0} mb={data.length === index + 1 ? 10 : 15}>
                <Avatar
                  size={40}
                  radius="xl"
                  src={item.avatar_url}
                  component="span"
                  sx={{ cursor: "pointer" }}
                  onClick={() => openProfile(item.username)}
                />
                <Box ml={20} sx={{ cursor: "pointer", flex: 1 }} onClick={() => openProfile(item.username)}>
                  <Text weight={700} p={0} lineClamp={1} sx={{ lineHeight: 1.2 }}>
                    {item.full_name}
                  </Text>
                  <Text size="sm" lineClamp={1} sx={{ lineHeight: 1.2 }}>
                    @{item.username}
                  </Text>
                </Box>
                {typeof item.is_followed === "boolean" && ((type === "followers" && !item.is_me) || (type === "following" && item.is_me)) && (
                  <Button size="xs" color={item.is_followed ? "red" : undefined} onClick={() => onPressFollow(item)}>
                    {item.is_followed ? "Unfollow" : "Follow"}
                  </Button>
                )}
              </Group>
            );
          })}
        </Box>

        {!loading && data.length === 0 && (
          <Alert color="gray" radius="md" py={20} sx={{ textAlign: "center" }}>
            {"No results found"}
          </Alert>
        )}

        {!noMore.current && data.length > 0 && (
          <>
            {loading ? (
              <Loader color="dark" sx={{ width: "100%" }} />
            ) : (
              <Button
                variant="outline"
                radius="xl"
                my={30}
                style={{ left: "50%", transform: "translateX(-50%)" }}
                onClick={() => {
                  page.current = page.current + 1;
                  getData(type, term);
                }}
              >
                {"Load more"}
              </Button>
            )}
          </>
        )}
        
      </Modal>
    </Group>
  );
}
